import { AxiosError } from 'axios';
import { useEffect, type ReactNode } from 'react';

import { useCompanyQuery } from '@/hooks/queries/use-company-query';
import { isInvalidTenantError } from '@/lib/http-errors';
import { InvalidTenantPage } from '@/pages/InvalidTenantPage';
import { QueryErrorPage } from '@/pages/QueryErrorPage';
import { useTenantStore } from '@/stores/tenant';

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps) {
  const query = useCompanyQuery();
  const { data, isError, error, failureReason } = query;

  // Sync query result to Zustand for downstream consumers (LoginPage, AuthLayout, InvalidTenantPage)
  useEffect(() => {
    if (data) {
      useTenantStore.getState().setTenant(data);
    }
  }, [data]);

  // Only the tenant middleware's own 400/404 text reaches the store (TDEV-1107)
  useEffect(() => {
    if (isInvalidTenantError(error)) {
      const message =
        error instanceof AxiosError && error.response?.data?.message
          ? (error.response.data as { message: string }).message
          : 'Unable to validate workspace';
      useTenantStore.getState().setError(message);
    }
  }, [error]);

  if (isInvalidTenantError(error)) {
    return <InvalidTenantPage />;
  }

  // A failed background refetch keeps the loaded app; only a first load shows
  // the error. failureReason is set while a 429 is being retried.
  if (data === undefined) {
    if (isError || failureReason) {
      return <QueryErrorPage query={query} />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
